import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';
import { Link } from 'react-router-dom';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem,
    VirtualizedTableItemValue
} from 'services/virtualizedTable/types';

import { setColumnWidth } from 'services/columnWidths/actions';
import VirtualizedTable from 'components/VirtualizedTable';
import QueryStringLink from 'components/QueryStringLink';

import {
    selectSubmissionTableColumns,
    makeSelectSubmissionItems
} from './selectors';
import { SPRAY_SUBMISSION_VIEW_PAYLOAD_MODAL } from '../../constants';

interface OwnProps {
    tableId: string;
    entityTypeId?: number;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class SpraySubmissionProductsVirtualizedTableComponent extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    itemValueFormat = (
        columnKey: VirtualizedTableColumnKey,
        value: VirtualizedTableItemValue
    ): VirtualizedTableItemValue => {
        const { entityTypeId } = this.props;
        let formattedValue = value;

        if (columnKey === 'sprayEntities') {
            const entities = value.split(', ');

            formattedValue = entities
                .map((entity: string) => (
                    <Link
                        key={entity}
                        to={`/organization/areas/${entityTypeId}?search=${entity}`}
                        target="_blank"
                    >
                        {entity}
                    </Link>
                ))
                .reduce(
                    (prev: React.ReactElement, current: React.ReactElement) => [
                        prev,
                        ', ',
                        current
                    ]
                );
        } else if (columnKey === 'payload') {
            formattedValue = (
                <QueryStringLink
                    params={{
                        [SPRAY_SUBMISSION_VIEW_PAYLOAD_MODAL]: true,
                        spraySubmissionId: value
                    }}
                >
                    See details
                </QueryStringLink>
            );
        }

        return formattedValue;
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                label="Product"
                labelPlural="Products"
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                itemValueFormat={this.itemValueFormat}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectSubmissionItems(),
    columns: selectSubmissionTableColumns
});

export default connect(mapStateToProps, {
    setColumnWidth
})(SpraySubmissionProductsVirtualizedTableComponent);
